/**
 * A person's face — their initials in their own tone (`docs/activity.md` §3,
 * §4.5) — and a row of them overlapping, as every Activity board draws it:
 * a 2 px white ring, each one 6 px over the one before, up to `max` then
 * *+n*.
 */
import { toneOf } from '@rowted/shared'
import { cn } from '../lib/cn.js'

export interface FacePerson {
  id: string
  name: string
  initials: string
  colour: string | null
}

/** The boards' initials: 6.3 px in a 15 px circle, 7.1 in 17, 10 from 18 to 28, then a third of the size. */
const faceFont = (size: number): number => Math.round((size < 18 ? size * 0.42 : Math.max(10, size * 0.34)) * 10) / 10

export function Face({ person, size = 22, className, self = false }: {
  person: FacePerson; size?: number; className?: string
  /** The viewer's own face: the accent ring the product gives *you* (Board 13 C: *Alex Kerr (you)*). */
  self?: boolean
}) {
  const tone = toneOf(person.colour)
  return (
    <span
      title={person.name}
      className={cn('inline-flex shrink-0 items-center justify-center rounded-full font-bold tracking-[.02em]',
        self && 'bg-accent-ring text-accent-dark', className)}
      style={{ width: size, height: size, fontSize: faceFont(size), ...(self ? {} : { background: tone.tint, color: tone.text }) }}
    >
      {person.initials}
    </span>
  )
}

/** The dashed face of an Unassigned (Board 1 C, Board 8 B). */
export function UnassignedFace({ size = 26 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="inline-flex shrink-0 items-center justify-center rounded-full border-[1.5px] border-dashed border-line-strong bg-surface text-text-muted"
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 24 24" width={Math.round(size * 0.54)} height={Math.round(size * 0.54)} fill="none"
        stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-6l-2 3h-4l-2-3H2" />
        <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
      </svg>
    </span>
  )
}

export function FaceStack({
  people, size = 22, max = 6, ring = 'white', className, overlap = 6,
}: {
  people: readonly FacePerson[]
  size?: number
  max?: number
  ring?: 'white' | 'none'
  className?: string
  /** How far each face sits over the one before (the phone's faces button: 7). */
  overlap?: number
}) {
  const shown = people.slice(0, max)
  const more = people.length - shown.length
  return (
    <span className={cn('inline-flex items-center', className)}>
      {shown.map((person, index) => (
        <span
          key={person.id}
          className={cn('inline-flex rounded-full', ring === 'white' && 'border-2 border-white bg-white')}
          style={{ marginLeft: index === 0 ? 0 : -overlap }}
        >
          <Face person={person} size={size} />
        </span>
      ))}
      {more > 0 && (
        <span className="ml-1 text-[10.5px] font-semibold text-text-muted">+{more}</span>
      )}
    </span>
  )
}
